import React, { useCallback } from 'react';
import { Pack, Sample } from 'types';
import { SampleType } from 'types/enums';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { useNotificationsContext } from 'contexts/NotificationsContext/NotificationsContext';
import { analytics } from 'utils/analytics';
import { CreatorSampleType } from 'controllers/business/Creator/Creator.types';
import { RemovedAllFromCreatorSuccess } from 'contexts/NotificationsContext/notifications';

const useRemoveFromCreatorButton = ({
    sample,
    onClick,
}: {
    sample?: CreatorSampleType | Sample;
    onClick?: () => void;
}): {
    handleClick: (event: React.MouseEvent) => void;
} => {
    const {
        removeTrack,
        closeCreator,
        openRemoveAllModal,
        closeRemoveAllModal,
        dispatch,
        removeAllTracks,
        state: {
            samplesInSession,
            isRemoveAllModalOpen,
            bpm,
            samples,
            keySign,
            instrumentationSessionId,
            keyRoot,
            keyQuality,
        },
    } = useCreatorContext();

    const { createNotification } = useNotificationsContext();

    const handleRemoveAllTracks = useCallback((): void => {
        // TODO: hoist this up
        dispatch({
            type: 'removeAllSamples',
        });
        dispatch({ type: 'newInstrumentationSessionId' });

        removeAllTracks();
        closeRemoveAllModal();
        closeCreator();

        analytics.trackCreatorUpdated({
            creatorBpm: bpm,
            creatorKey: `${keyRoot} ${keyQuality} - ${keySign}`,
            deletedCreatorSession: true,
            numberSamplesInCreator: 0,
            sampleAdded: false,
            sampleRemoved: false,
            creatorSessionId: instrumentationSessionId,
        });

        createNotification(new RemovedAllFromCreatorSuccess());
    }, [
        closeCreator,
        samplesInSession,
        closeRemoveAllModal,
        createNotification,
        dispatch,
        removeAllTracks,
        keyRoot,
        keyQuality,
        instrumentationSessionId,
        bpm,
        keySign,
    ]);

    const handleRemove = useCallback(() => {
        if (!sample) {
            isRemoveAllModalOpen
                ? handleRemoveAllTracks()
                : openRemoveAllModal();
        } else {
            removeTrack(sample.guid, sample.name);

            analytics.trackCreatorUpdated({
                creatorBpm: bpm,
                creatorKey: `${keyRoot} ${keyQuality} - ${keySign}`,
                deletedCreatorSession: false,
                numberSamplesInCreator: samples.size || 0,
                sampleAdded: false,
                sampleRemoved: true,
                creatorSessionId: instrumentationSessionId,
                samplePackId: sample.pack.guid,
                samplePackName: sample.pack.name,
                assetArtist: sample.pack.artist?.guid,
                assetBpm: sample.bpm,
                assetId: sample.guid,
                assetInstrument: sample.instrumentFamily?.code,
                assetKey: sample.key?.code,
                assetLength: sample.duration || undefined,
                assetName: sample.name,
                assetGenre: (sample.pack as Pack)?.mainGenre?.code,
                assetLabel: (sample.pack as Pack)?.label?.guid || undefined,
                assetLoopType: sample.sampleType as SampleType,
            });
        }
    }, [
        handleRemoveAllTracks,
        isRemoveAllModalOpen,
        openRemoveAllModal,
        sample,
        removeTrack,
        instrumentationSessionId,
        bpm,
        keySign,
        samples,
        keyRoot,
        keyQuality,
    ]);

    const handleClick = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            handleRemove();
            onClick && onClick();
        },
        [onClick, handleRemove],
    );

    return {
        handleClick,
    };
};

export default useRemoveFromCreatorButton;
